import React from 'react';
import { View } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useQueue } from '../contexts/QueueContext';
import { 
    LayoutDashboard, PenTool, Search, Activity, Wrench, Zap, Swords, 
    User, LogOut, Wallet, HardDrive, Crown, BarChart2, Globe, 
    Loader2, ChevronRight, Settings, Command, UsersRound, Bot, Map as MapIcon,
    AlertCircle, Calendar, BrainCircuit
} from 'lucide-react';
import CommandPalette from './CommandPalette';

interface LayoutProps {
  currentView: View;
  setCurrentView: (view: View) => void;
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ currentView, setCurrentView, children }) => {
  const { user, profile, logout, role } = useAuth();
  const { toasts, removeToast } = useToast();
  const { isProcessing, progress } = useQueue();
  
  const [isApiKeyMissing, setIsApiKeyMissing] = React.useState(false);

  React.useEffect(() => {
    fetch('/api/diagnostics')
      .then(res => res.json())
      .then(data => {
        setIsApiKeyMissing(!data.aiConfigured);
      })
      .catch(() => {
        setIsApiKeyMissing(true);
      });
  }, []);

  const allNavItems = [
    { view: View.DASHBOARD, label: 'Overview', icon: LayoutDashboard, allowed: ['viewer', 'editor', 'admin'] },
    { view: View.CONTENT_PLANNER, label: 'Strategy & Planner', icon: Calendar, allowed: ['editor', 'admin'] },
    { view: View.WRITER, label: 'Content Studio', icon: PenTool, allowed: ['editor', 'admin'] },
    { view: View.LIBRARY, label: 'Lifecycle & Library', icon: Activity, allowed: ['editor', 'admin'] },
    { view: View.BRAND_VOICE, label: 'Brain & Connections', icon: BrainCircuit, allowed: ['editor', 'admin'] },
  ];

  const navItems = allNavItems.filter(item => item.allowed.includes(role));

  return (
    <div className="flex h-screen bg-stone-50 overflow-hidden text-stone-900 font-sans selection:bg-brand-500/20">
      <CommandPalette setView={setCurrentView} />

      {/* Sidebar - Auto Width based on Content (min 260px) */}
      <aside className="hidden lg:flex flex-col w-fit min-w-[260px] bg-stone-950 text-stone-400 z-30 shrink-0 border-r border-stone-800 transition-[width] duration-200 ease-in-out">
        <div className="h-16 flex items-center px-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center text-black shadow-[0_0_15px_rgba(255,255,255,0.2)] shrink-0">
                <Crown size={16} fill="currentColor" />
            </div>
            <div className="whitespace-nowrap overflow-hidden">
              <h1 className="text-sm font-bold text-white tracking-tight leading-none">SEO Master</h1>
              <span className="text-[10px] font-medium text-stone-500 block mt-0.5">Enterprise</span>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto custom-scrollbar">
          {navItems.map((item) => {
            const isActive = currentView === item.view 
              || (item.view === View.CONTENT_PLANNER && (currentView === View.KEYWORD_RESEARCH || currentView === View.COMPETITOR || currentView === View.CONTENT_GAP))
              || (item.view === View.COMPETITOR && currentView === View.CONTENT_GAP);
            return (
              <button 
                key={item.view} 
                onClick={() => setCurrentView(item.view)} 
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-[13px] font-medium group relative whitespace-nowrap
                    ${isActive 
                        ? 'bg-white/10 text-white shadow-sm ring-1 ring-white/10' 
                        : 'hover:bg-white/5 hover:text-stone-200 text-stone-400'}`}
              >
                <item.icon size={18} strokeWidth={isActive ? 2.5 : 2} className={`shrink-0 transition-colors ${isActive ? 'text-brand-500' : 'text-stone-500 group-hover:text-stone-300'}`} />
                <span>{item.label}</span>
                {isActive && <div className="ml-auto w-1 h-1 rounded-full bg-brand-500 shadow-[0_0_8px_rgba(245,158,11,0.8)]"></div>}
              </button>
            )
          })}
        </nav>

        <div className="p-4 border-t border-white/5 space-y-2">
            <div 
                onClick={() => setCurrentView(View.WALLET)}
                className="bg-white/5 rounded-xl p-3 border border-white/5 hover:bg-white/10 transition-colors cursor-pointer group flex items-center gap-3"
                title="Quản lý Credits & Billing"
            >
                <div className="w-8 h-8 rounded-full bg-stone-800 flex items-center justify-center overflow-hidden border border-stone-700 shrink-0">
                    {user?.photoURL ? <img src={user.photoURL} className="w-full h-full object-cover"/> : <User size={14} className="text-stone-500"/>}
                </div>
                <div className="flex-1 min-w-0 whitespace-nowrap overflow-hidden">
                    <p className="text-[12px] font-bold text-stone-200 truncate">{profile?.displayName || 'User'}</p>
                    <p className="text-[10px] text-brand-500 font-medium tracking-wide flex items-center gap-1">
                        <Wallet size={11}/> {profile?.credits || 0} CR <span className="text-stone-500 text-[9px] hover:underline">· Nạp</span>
                    </p>
                </div>
                <button 
                    onClick={(e) => { e.stopPropagation(); logout(); }} 
                    className="text-stone-500 hover:text-white transition-colors shrink-0 p-1"
                    title="Đăng xuất"
                >
                    <LogOut size={16}/>
                </button>
            </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col relative bg-stone-50 overflow-hidden">
        {/* API Key Warning */}
        {isApiKeyMissing && (
            <div className="bg-red-50 border-b border-red-100 px-6 py-2 flex items-center justify-between animate-fade-in">
                <div className="flex items-center gap-2 text-red-700 text-[11px] font-bold uppercase tracking-wider">
                    <AlertCircle size={14} />
                    <span>Cảnh báo: Thiếu GEMINI_API_KEY. Các tính năng AI sẽ không hoạt động.</span>
                </div>
                <a href="https://ai.google.dev/" target="_blank" rel="noopener noreferrer" className="text-[10px] bg-red-600 text-white px-3 py-1 rounded-full font-bold hover:bg-red-700 transition-colors">Lấy Key ngay</a>
            </div>
        )}

        {/* Processing Indicator */}
        {isProcessing && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-stone-950 text-white px-5 py-2.5 rounded-full flex items-center gap-4 shadow-2xl border border-white/10 animate-slide-up">
                <div className="flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-wide">
                    <Loader2 size={12} className="animate-spin text-brand-500"/>
                    <span className="text-stone-300">Processing: <span className="text-white">{progress}%</span></span>
                </div>
                <button onClick={() => setCurrentView(View.AUTO_WRITER)} className="ml-2 w-5 h-5 flex items-center justify-center bg-stone-800 rounded-full hover:bg-stone-700 transition-colors"><ChevronRight size={12}/></button>
            </div>
        )}

        <div className="flex-1 overflow-hidden relative">
            {children}
        </div>
      </main>

      {/* Toast Notifications */}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-3 pointer-events-none">
          {toasts.map((toast) => (
              <div key={toast.id} className="pointer-events-auto bg-white rounded-lg border border-stone-200 shadow-premium p-4 flex gap-4 min-w-[320px] max-w-sm animate-slide-up items-start">
                  <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${toast.type === 'success' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : toast.type === 'error' ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]' : 'bg-brand-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]'}`}></div>
                  <div className="flex-1">
                      <h4 className="text-[13px] font-bold text-stone-900 leading-tight">{toast.message}</h4>
                      {toast.description && <p className="text-[11px] text-stone-500 mt-1 leading-relaxed">{toast.description}</p>}
                  </div>
                  <button onClick={() => removeToast(toast.id)} className="text-stone-300 hover:text-stone-900 transition-colors"><ChevronRight size={14} /></button>
              </div>
          ))}
      </div>
    </div>
  );
};

export default Layout;